/**
 * Prompt 5 + 4: the two selections the design requires to be sticky.
 *
 *   centerView  PLAYERS | BOARD -- "persist per user per draft and restore it on reload.
 *               Do NOT auto-switch the view when the user comes on the clock."
 *   railTab     QUEUE | FEED | CHAT -- "remember the last selection per session".
 *
 * Keyed per league so two drafts do not share one preference, and namespaced `sf_` to match
 * the existing localStorage keys in this app (sf_active_nfl_league_id and friends).
 *
 * Every read is defensive. localStorage throws in private-mode Safari and in some embedded
 * webviews, and a draft room that fails to mount because a preference could not be read
 * would be a far worse bug than losing the preference.
 */

export type CenterView = 'players' | 'board';
export type RailTab = 'queue' | 'feed' | 'chat';

const CENTER_VIEWS: readonly CenterView[] = ['players', 'board'];
const RAIL_TABS: readonly RailTab[] = ['queue', 'feed', 'chat'];

const centerKey = (leagueId: string) => `sf_nfl_draft_center_view:${leagueId}`;
const railKey = (leagueId: string) => `sf_nfl_draft_rail_tab:${leagueId}`;

function read(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* storage unavailable -- the preference is lost, the room still works */
  }
}

const hideDraftedKey = (leagueId: string) => `sf_nfl_draft_hide_drafted:${leagueId}`;

/**
 * SAB-932: hide already-drafted players from the pool while BROWSING.
 *
 * Defaults to TRUE — hidden. That is the conventional fantasy behaviour (Sleeper removes
 * drafted players; ESPN's "Show drafted" checkbox is off by default; Yahoo and NFL.com
 * drop them from the available list) and it is what a drafter wants for all but a handful
 * of the picks in a 190-pick draft.
 *
 * It never suppresses a SEARCH hit — that rule lives in filterDraftPool, not here, so this
 * module stays a dumb store. See filterDraftPool's header for why the two tickets pull
 * apart and how they reconcile.
 *
 * Persisted rather than reset per draft: this is a preference about how someone likes to
 * read a board, not a fact about this draft. Keyed per league like the others, so a manager
 * in two leagues can differ. localStorage, therefore per-device and per-user — which is
 * what makes it commissioner-independent, as SAB-932 requires.
 *
 * The POSITION filter is deliberately NOT persisted. Restoring "only QBs" on reload would
 * look like an empty pool, and the legacy room does not persist it either.
 */
export function loadHideDrafted(leagueId: string | null | undefined, fallback = true): boolean {
  if (!leagueId) return fallback;
  const v = read(hideDraftedKey(leagueId));
  // Only the two literals are honoured. Anything else -- absent, or a stale key from an
  // earlier build -- falls back rather than being coerced, so a junk value cannot pin the
  // pool into a state the user did not choose.
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

export function saveHideDrafted(leagueId: string | null | undefined, hidden: boolean): void {
  if (!leagueId) return;
  write(hideDraftedKey(leagueId), hidden ? 'true' : 'false');
}

export function loadCenterView(leagueId: string | null | undefined, fallback: CenterView = 'players'): CenterView {
  if (!leagueId) return fallback;
  const v = read(centerKey(leagueId));
  // Validate against the union rather than casting. A stale key from an earlier build
  // holding e.g. "recap" would otherwise put the center column into a state that renders
  // nothing, and it would persist across reloads because it is stored.
  return (CENTER_VIEWS as readonly string[]).includes(v ?? '') ? (v as CenterView) : fallback;
}

export function saveCenterView(leagueId: string | null | undefined, view: CenterView): void {
  if (!leagueId) return;
  write(centerKey(leagueId), view);
}

export function loadRailTab(leagueId: string | null | undefined, fallback: RailTab = 'queue'): RailTab {
  if (!leagueId) return fallback;
  const v = read(railKey(leagueId));
  return (RAIL_TABS as readonly string[]).includes(v ?? '') ? (v as RailTab) : fallback;
}

export function saveRailTab(leagueId: string | null | undefined, tab: RailTab): void {
  if (!leagueId) return;
  write(railKey(leagueId), tab);
}
